import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import { DuChefMark } from './authGraphics';
import { ReasonsList } from './ReasonsList';

/**
 * Desktop-only left brand panel: cream wash, logo lockup, display headline,
 * the three reasons, and the "Fait maison · à Montréal" footer. Below the
 * 820px breakpoint the screen hides this and renders <ReasonsList variant="header" />
 * above the form instead.
 *
 * expo-linear-gradient can't do a radial, so the handoff's radial cream is
 * approximated with a soft diagonal from brandTint to background.
 */
export const BrandPanel: React.FC = () => {
  const { t } = useTranslation();

  return (
    <LinearGradient
      colors={[Paprika.brandTint, Paprika.background]}
      start={{ x: 0.1, y: 0 }}
      end={{ x: 0.75, y: 0.9 }}
      style={styles.panel}
    >
      {/* decorative ghost chili, bottom-right */}
      <View style={styles.ghost} pointerEvents="none">
        <DuChefMark size={320} opacity={0.08} />
      </View>

      {/* logo */}
      <View style={styles.logoRow}>
        <DuChefMark size={28} />
        <Text style={styles.wordmark}>Du Chef</Text>
      </View>

      {/* headline + reasons */}
      <View>
        <Text style={styles.headline}>{t('auth.brandHeadline')}</Text>
        <View style={styles.reasons}>
          <ReasonsList variant="panel" />
        </View>
      </View>

      {/* footer */}
      <Text style={styles.footer}>{t('auth.madeIn')}</Text>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    paddingHorizontal: 48,
    paddingVertical: 56,
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  ghost: {
    position: 'absolute',
    right: -60,
    bottom: -40,
    transform: [{ rotate: '-12deg' }],
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  wordmark: {
    fontFamily: 'Newsreader-Bold',
    fontSize: 23,
    letterSpacing: -0.2,
    color: Paprika.textPrimary,
  },
  headline: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 40,
    lineHeight: 44,
    letterSpacing: -0.6,
    color: Paprika.textPrimary,
    maxWidth: 240,
  },
  reasons: {
    marginTop: 30,
  },
  footer: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
});
